import React, { useEffect } from 'react';

import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import SvgPhone from '@icons/phone-filled.svg?react';
import { SecuredConfig } from '@components/security/securedDecoration';
import RingilTextArea from '@components/framework/input/RingilTextArea';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import * as yup from 'yup';
import { findErrorMessage } from '@utils/validationUtils.ts';

interface ComponentProps {
  phone: string;
  onChange: (phone: string) => void;
  readOnly?: boolean;
  required?: boolean;
  maxLength?: number;
  dataTestId?: string;
  secured?: SecuredConfig;
}

interface Form {
  phone: string;
}

const PhoneTag: React.FC<ComponentProps> = ({ phone, onChange, readOnly, required = false, maxLength, dataTestId, secured }) => {
  const { tr } = useTranslationLgs();
  const requiredMessage = tr('validation.required', 'Value is required');
  const displayValue = (phone ?? '').trim() === '' ? undefined : phone;

  const form = useForm<Form>(
    yup.object().shape({
      phone: required ? yup.string().nullable().required(requiredMessage).matches(/\S/, requiredMessage) : yup.string().nullable(),
    }),
    { phone },
    (values, onSuccess) => save(values, onSuccess),
    false,
    false,
    null,
    'none',
  );

  useEffect(() => {
    cancel();
  }, [phone]);

  const cancel = () => {
    form.clear();
    form.overwriteValues({ phone });
  };

  const save = (values: Form, onSuccess?: () => void) => {
    if (onSuccess) onSuccess();
    onChange(values.phone);
  };

  const pane = () => {
    return (
      <VerticalFieldAndValue
        label={tr(`PhoneTag.title`, `Phone`)}
        labelMode={'normal'}
        required={required}
        errorMessage={findErrorMessage(form.validationErrors, 'phone', tr)}
      >
        <RingilTextArea
          rows={1}
          value={form.values.phone}
          onChange={v => form.setFieldValue(form.names.phone, v)}
          maxLength={maxLength}
          className={'resize-none'}
        />
      </VerticalFieldAndValue>
    );
  };

  return (
    <>
      <ButtonTag
        autoClose
        label={tr(`PhoneTag.title`, `Phone`)}
        value={displayValue}
        icon={<SvgPhone />}
        actionIcon={<SvgTagEdit />}
        actionPane={pane()}
        onSave={onSuccess => form.validateAndSendWithSuccess(onSuccess)}
        readonly={readOnly}
        required={required}
        onClose={() => cancel()}
        dataTestId={dataTestId}
        secured={secured}
      />
    </>
  );
};

export default PhoneTag;
